<template setup>
  <div class="cooperation-page">
    <div class="cooperation-header">
      <ContainerCom contentId="cooperation-header" backgroundSize="cover" backgroundPosition="center">
        <text-com class="cooperation-header-title" contentId="cooperation-header-title"></text-com>
      </ContainerCom>
    </div>

    <div class="cooperation-container">
      <div class="cooperation-form-wrapper">
        <div class="form-left">
          <el-form
            :model="formData"
            :rules="rules"
            ref="formRef"
            class="cooperation-form"
            label-width="0"
          >
            <el-row :gutter="20">
              <el-col :span="8" :xs="24" :sm="12" :md="8">
                <el-form-item prop="name">
                  <el-input
                    size="large"
                    v-model="formData.name"
                    :placeholder="'*' + t('cooperation.namePlaceholder')"
                    class="custom-input"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24" :sm="12" :md="8">
                <el-form-item prop="company">
                  <el-input
                    size="large"
                    v-model="formData.company"
                    :placeholder="'*' + t('cooperation.companyPlaceholder')"
                    class="custom-input"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24" :sm="24" :md="8">
                <el-form-item prop="phoneNumber">
                  <el-input
                    size="large"
                    v-model="formData.phoneNumber"
                    :placeholder="'*' + t('cooperation.phoneNumberPlaceholder')"
                    class="custom-input"
                  ></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item prop="cooperationIntention">
              <el-input
                v-model="formData.cooperationIntention"
                type="textarea"
                resize="none"
                :rows="5"
                :placeholder="'*' + t('cooperation.cooperationIntentionPlaceholder')"
              ></el-input>
            </el-form-item>
          </el-form>
        </div>
        <div class="form-right">
          <h2 class="form-title">{{ t('cooperation.formTitle') }}</h2>
          <div class="form-description">{{ t('cooperation.formDescription') }}</div>
          <div class="form-actions">
            <button
              type="primary"
              round
              @click="submitForm"
              :loading="submitting"
              :disabled="submitting"
              class="submit-button"
            >
              {{ t("cooperation.submitButton") }}
              <el-icon class="button-arrow"><arrow-right /></el-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="map-section">
      <div class="map-overlay"></div>
      <MapComponent
        ref="mapRef"
        :width="'100%'"
        :height="'900px'"
        :center="centerPoint"
        :zoom="zoomLevel"
        :map-type="mapType"
        @map-ready="onMapReady"
        @zoom-change="onZoomChange"
      />
      <div v-show="!isVisible" class="toggle-row" :style="'padding: 12px'">
        <div
          class="toggle-button"
          @click="toggleVisibility"
          :title="isVisible ? '最小化' : '全屏化'"
        >
          <span class="toggle-icon">{{ isVisible ? "一" : "⛶" }}</span>
          <!-- <span class="toggle-text">{{ isVisible ? '最小化' : '全屏化' }}</span> -->
        </div>
      </div>
      <ContactInfo
        class="contact-section"
        :isVisible="isVisible"
        @toggleVisibility="toggleVisibility"
        :locations="contactLocations"
        :location-details="contactDetails"
        :default-location-id="defaultLocation"
        @location-change="handleLocationChange"
      />
    </div>
  </div>
</template>

<script setup>
import { nextTick, reactive, ref, useTemplateRef, computed, inject } from "vue";
import { ElMessage } from "element-plus";
import { useI18n } from "vue-i18n";
import ContainerCom from "../components/ContainerCom.vue";
import cooperationAPI from "../api/modules/cooperation.js";
import MapComponent from "../components/MapComponent.vue";
import ContactInfo from "../components/ContactInfo.vue";
import TextCom from "../components/TextCom.vue";

// 注入联系信息
const contactInfo = inject("contactInfo", ref([]));
// 使用i18n
const { t } = useI18n();
const formRef = ref();
const submitting = ref(false);

// 地图组件相关
const mapRef = useTemplateRef("mapRef");
// 初始中心点坐标，优先使用第一个联系信息的坐标
const centerPoint = computed(() => {
  if (contactInfo.value.length > 0) {
    const firstContact = contactInfo.value[0];
    let lat = firstContact.latitude && !isNaN(parseFloat(firstContact.latitude)) ? parseFloat(firstContact.latitude) : 28.0011;
    let lng = firstContact.longitude && !isNaN(parseFloat(firstContact.longitude)) ? parseFloat(firstContact.longitude) : 120.7065;
    return [lat + 0.015, lng]; // 与handleLocationChange保持一致的偏移量
  }
  return [28.0011 + 0.015, 120.7065]; // 默认坐标
});
const zoomLevel = ref(13);
const mapType = ref("vec"); // vec:矢量图, img:卫星图, ter:地形图
const isVisible = ref(true);

// 注入当前语言
const currentLanguage = inject("currentLanguage", ref("zh"));

// 联系信息数据 - 从注入的contactInfo获取
const contactLocations = computed(() => {
  return contactInfo.value.map((info, index) => {
    const locationId = info.id?.toString() || `location_${index}`;
    return {
      id: locationId,
      name: currentLanguage.value === "en" ? (info.contactNameEn || info.contactName) : info.contactName,
      country: currentLanguage.value === "en" ? (info.countryEn || info.country) : info.country
    };
  });
});

const contactDetails = computed(() => {
  const details = {};
  contactInfo.value.forEach((info, index) => {
    const locationId = info.id?.toString() || `location_${index}`;
    details[locationId] = {
      address: currentLanguage.value === "en" ? (info.addressEn || info.address) : info.address,
      phone: info.phone,
      email: info.email
    };
  });
  return details;
});

// 地点中心点坐标和缩放级别
const locationData = computed(() => {
  const data = {};
  contactInfo.value.forEach((info, index) => {
    const locationId = info.id?.toString() || `location_${index}`;
    // 尝试解析经纬度，如果解析失败使用默认值
    let lat = 28.0011;
    let lng = 120.7065;
    if (info.latitude && !isNaN(parseFloat(info.latitude))) {
      lat = parseFloat(info.latitude);
    }
    if (info.longitude && !isNaN(parseFloat(info.longitude))) {
      lng = parseFloat(info.longitude);
    }
    data[locationId] = {
      center: [lat, lng], // [lat, lng]
      zoom: zoomLevel.value,
    };
  });
  return data;
});

// 默认地点设置为第一个可用地点
const defaultLocation = computed(() => {
  return contactLocations.value.length > 0 ? contactLocations.value[0].id : "";
});

// 地图就绪回调
const onMapReady = (map) => {
  console.log("地图已就绪", map);
  // 初始化时添加默认地点的标记
  addLocationMarker(defaultLocation.value);
};

// 为指定地点添加标记
const addLocationMarker = (locationId) => {
  if (!mapRef.value || !locationData.value[locationId]) return;

  const locationInfo = locationData.value[locationId];
  const details = contactDetails.value[locationId];

  // 清除现有标记
  mapRef.value.clearMarkers();
  // 添加新标记
  const marker = mapRef.value.addMarker(
    locationInfo.center[0], // lat
    locationInfo.center[1], // lng
    {
      title: `${
        contactLocations.value.find((loc) => loc.id === locationId)?.name
      }, ${
        contactLocations.value.find((loc) => loc.id === locationId)?.country
      }`,
    }
  );

  // 添加弹出信息
  // if (marker && details) {
  //   let popupContent = `
  //     <div style="font-size: 14px;">
  //       <strong>${
  //         contactLocations.value.find((loc) => loc.id === locationId)?.name
  //       }</strong><br>
  //       <div style="margin-top: 5px; line-height: 1.5;">
  //         <p><strong>地址:</strong> ${details.address}${
  //     details.addressLine2 ? "<br>" + details.addressLine2 : ""
  //   }</p>
  //         <p><strong>电话:</strong> ${details.phone}</p>
  //         <p><strong>邮箱:</strong> ${details.email}</p>
  //       </div>
  //     </div>
  //   `;
  //   marker.bindPopup(popupContent).openPopup();
  // }
};

// 处理地点切换事件
const handleLocationChange = (locationId) => {
  console.log("Location changed to:", locationId);

  if (locationData.value[locationId]) {
    // 更新地图中心点和缩放级别
    mapRef.value && mapRef.value.setView(
      locationData.value[locationId].center[0] + 0.015, // lat
      locationData.value[locationId].center[1], // lng
      locationData.value[locationId].zoom
    );

    // 添加标记点
    addLocationMarker(locationId);
  }
};
const formData = reactive({
  name: "",
  company: "",
  position: "",
  phoneNumber: "",
  email: "",
  cooperationIntention: "",
});

const rules = {
  name: [
    { required: true, message: t("cooperation.nameRequired"), trigger: "blur" },
    { min: 1, max: 50, message: t("cooperation.nameLength"), trigger: "blur" },
  ],
  company: [
    {
      required: true,
      message: t("cooperation.companyRequired"),
      trigger: "blur",
    },
    {
      min: 1,
      max: 100,
      message: t("cooperation.companyLength"),
      trigger: "blur",
    },
  ],
  phoneNumber: [
    {
      required: true,
      message: t("cooperation.phoneNumberRequired"),
      trigger: "blur",
    },
    {
      pattern: /^1[3-9]\d{9}$/,
      message: t("cooperation.phoneNumberFormat"),
      trigger: "blur",
    },
  ],
  cooperationIntention: [
    {
      required: true,
      message: t("cooperation.cooperationIntentionRequired"),
      trigger: "blur",
    },
    {
      min: 10,
      max: 1000,
      message: t("cooperation.cooperationIntentionLength"),
      trigger: "blur",
    },
  ],
};
const toggleVisibility = () => {
  isVisible.value = !isVisible.value;
  // if (!isVisible.value) {
  //   // 展开时，设置联系信息部分的宽度为 fit-content
  //   setTimeout(() => {
  //     document.querySelector('.contact-section').style.width = 'fit-content';
  //   },2000)
  // } else {
  //   // 折叠时，设置联系信息部分的宽度为 0
  //   setTimeout(() => {
  //     document.querySelector('.contact-section').style.width = '100px';
  //   },2000)
  // }
};
const submitForm = async () => {
  if (!formRef.value) return;

  try {
    await formRef.value.validate();
    submitting.value = true;

    const response = await cooperationAPI.submitCooperation(formData);

    if (response.success) {
      ElMessage.success(t("cooperation.submitSuccess"));
      resetForm();
    } else {
      ElMessage.error(t("cooperation.submitError") + (response.message || ""));
    }
  } catch (error) {
  } finally {
    submitting.value = false;
  }
};

const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

// const onZoomChange = (zoom) => {
//   console.log("缩放级别变化:", zoom);
//   zoomLevel.value = zoom;
// };

const onCenterChange = (center) => {
  console.log("中心坐标变化:", center);
  centerPoint.value = center;
};
</script>

<style scoped>
.cooperation-page {
  min-height: 100vh;
  background-color: #fafafa;
  position: relative;
  /* padding-bottom: 100px; */
}

.cooperation-header {
  text-align: center;
  /* position: absolute; */
  left: 0;
  top: 0px;
  width: 100vw;
  height: 60vh;
  z-index: 1;
}

/* 只为背景图片添加放大动画 */
.cooperation-header :deep(.background-img) {
  animation: bgScaleUp 3s ease-out forwards;
}

@keyframes bgScaleUp {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.05);
  }
}

.cooperation-header-title {
  /* 上下，左右居中 */
  position: absolute;
  left: 15%;
  top: 40%;
  transform: translate(-50%, -50%);
  font-size: 4rem;
  color: #fff;
  letter-spacing: 5px;
  @media (max-width: 768px) {
    font-size: 3rem;
    left: 50%;
    /* transform: translate(-50%, -50%); */
    width: 80%;
    /* letter-spacing: 1px; */
    text-align: center;
  }
}

.cooperation-container {
  /* margin: 0 auto; */
  /* padding: 0px 30px; */
  /* border-radius: 12px; */
  overflow: hidden;
  background-color: rgba(236, 240, 244, 0);
  position: absolute;
  top: 0;
  left: 50%;
  /* transform: translateX(-25%); */
  z-index: 500;
  transform: translate(-50%, 40vh);
  /* padding-bottom: 80px; */
  /* max-width: 1200px; */
  width: 80vw;
  /* 响应式设计 */
  @media (max-width: 480px) {
    width: 95vw;
  }
}

.cooperation-form-wrapper {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
  margin-bottom: 2rem;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  gap: 4rem;
  align-items: flex-start;
}

.form-left {
  flex: 1;
}

.form-right {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: flex-start;
  /* padding-top: 20px; */
  min-width: 250px;
}

.form-title {
  font-size: 1.8rem;
  color: #2c3e50;
  margin: 0;
  font-weight: 600;
}

.form-description {
  font-size: 1rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.form-actions {
  display: flex;
  justify-content: flex-start;
}

/* 自定义输入框样式 */
.custom-input {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  height: 50px;
}

/* .custom-input:hover {
  border-color: #1890ff;
} */

.custom-input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* 自定义文本域样式 */
.custom-textarea {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.custom-textarea:hover {
  border-color: #1890ff;
}

.custom-textarea:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* 修复element-plus textarea的hover样式 */
:deep(.el-textarea__inner):hover {
  border-color: #1890ff;
  transition: all 0.3s ease;
}

/* 提交按钮样式 */
.submit-button {
  background-color: white;
  color: #000;
  border: 2px solid var(--el-border-color);
  padding: 10px 30px;
  font-size: 1rem;
  border-radius: 25px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
}

.submit-button:hover {
  background-color: #004a99;
  border-color: #004a99;
  color: #fff;
}

.submit-button:active {
  transform: scale(0.98);
}

.button-arrow {
  font-size: 0.9rem;
  transition: transform 0.3s ease;
}

.submit-button:hover .button-arrow {
  transform: translateX(4px);
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .cooperation-form-wrapper {
    flex-direction: column;
    gap: 2rem;
  }
  
  .form-right {
    align-items: flex-start;
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .cooperation-form-wrapper {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.5rem;
  }
  
  .form-right {
    gap: 1rem;
  }
  
  .submit-button {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .cooperation-form-wrapper {
    padding: 1rem;
  }
  
  .form-title {
    font-size: 1.3rem;
  }
  
  .form-description {
    font-size: 0.9rem;
  }
}

.form-title {
  font-size: 1.8rem;
  color: #2c3e50;
  /* margin-bottom: 2rem; */
  text-align: center;
  font-weight: 600;
}

.cooperation-form {
  /* max-width: 800px; */
  margin: 0 auto;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #2c3e50;
}

:deep(.el-input__wrapper) {
  transition: all 0.3s;
  border-radius: 8px !important;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.2) !important;
}

:deep(.el-button--primary) {
  padding: 0 2rem;
  height: 40px;
  font-size: 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

:deep(.el-button--primary:hover) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
}

:deep(.el-button) {
  padding: 0 2rem;
  height: 40px;
  font-size: 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

:deep(.el-button:hover) {
  transform: translateY(-2px);
}

/* 表单按钮组 */
:deep(.el-form-item__content .el-button-group) {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 20px;
}
:deep(.el-form-item__label) {
  display: none;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .cooperation-form-wrapper {
    padding: 2rem;
  }

  .form-title {
    font-size: 1.6rem;
  }
}

@media (max-width: 768px) {
  .cooperation-form-wrapper {
    padding: 1.5rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .cooperation-form {
    :deep(.el-form-item__label) {
      width: 100px;
    }
  }
}
.toggle-row {
  display: flex;
  justify-content: flex-end;
  position: absolute;
  top: 20%;
  right: 10%;
  z-index: 1009;
  @media (max-width: 480px) {
 top: 40%;
    right: 5%;
}
}
/* 切换按钮样式 */
.toggle-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #f0f9ff;
  border: 1px solid #e6f7ff;
  color: #1890ff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  width: fit-content;
  /* 在右上角 */

  /* float: right; */
}
.toggle-button:hover {
  background-color: #e6f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
}

.toggle-icon {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}
@media (max-width: 480px) {
  .cooperation-form-wrapper {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.3rem;
  }

  .cooperation-form {
    :deep(.el-form-item__label) {
      width: 80px;
      font-size: 0.9rem;
    }
  }

  /* 按钮容器的响应式样式 */
  :deep(.el-form-item__content > div) {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  :deep(.el-button--primary),
  :deep(.el-button) {
    width: 100%;
    margin-bottom: 0;
  }

  :deep(.el-form-item__content .el-button-group) {
    flex-direction: column;
    gap: 10px;
  }
}

/* 地图组件样式 */
.map-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  /* padding: 20px; */
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  /* margin-bottom: 2rem; */
  position: relative;
  overflow: hidden;
}

.map-section:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

/* 地图遮罩层 - 上部25%从上到下渐变 */
.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 35%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.8), transparent);
  /* 移除pointer-events: none，允许页面滑动而不是地图缩放 */
  z-index: 10; /* 确保遮罩层在地图上方 */
}

/* 响应式地图样式 */
@media (max-width: 768px) {
  .map-section {
    padding: 15px;
  }
}

@media (max-width: 480px) {
  .map-section {
    padding: 10px;
    border-radius: 8px;
  }
}

/* 联系信息部分样式 */
.contact-section {
  /* width: fit-content; */
  position: absolute;
  top: 20%;
  right: 10%;
  /* transform: translate(-50%, -50%); */
  z-index: 500;
  width: 500px;
}

/* 响应式联系信息部分 */
@media (max-width: 768px) {
  .contact-section {
    max-width: 90%;
    margin-top: 1.5rem;
    margin-bottom: 2rem;
  }
}

@media (max-width: 480px) {
  .contact-section {
    width: 95%;
    /* 居中 */
    top: 40%;
    right: 5%;
    margin-top: 1rem;
    margin-bottom: 1.5rem;
  }
}
</style>
